import { FacetTypes } from '../../../facets/Facets';
import { GlobalLabelUI } from "./GlobalLabelUI";
import { HudLayer } from '../HudLayer';
import { HUD_LAYER } from '../HudLayerConstants';
import { LevelLabelUI } from './LevelLabel';
import { LevelLabelPlacer } from './LevelPlacer';
import debounce from "../../../streams/timed-debounce";
import { flyToTopOfBox } from "../../../../lmv-compat/tools/FitToViewUtil";
import { sortedArrayHas } from '../../../facets/sortedArray';
import { PRIORITY } from "../../LabelCompositor";
import { getLevelElevationByName } from './LevelUtils';
import { DT_LOADER_IDLE_EVENT } from '../../../DtEventTypes';

import { av, THREE } from "@adsk/lmv";

const byElevation = (l, r) => {
  if (l.selected && !r.selected) return -1;else
  if (r.selected && !l.selected) return 1;
  return l.avgElev - r.avgElev;
};

/** Level labels, seen when looking from the side, and shifted away from the actual level. */
export class LevelsLayer extends HudLayer {

  /** @type {Map<string, LevelLabel>} Label by level facet ID (its common name). */
  labelById = new Map();

  /** @type {Array} List of filtered level facets */
  filteredLevels = [];

  /** Facility-wide target, as a last resort when deferring to level(s) is not possible. */
  globalTarget;

  constructor(facility, hud, viewer) {
    super(HUD_LAYER.LEVELS.id, facility, hud, viewer);

    this.placer = new LevelLabelPlacer(this, () => this.#invalidatePlacement(true));
    this.boundInvalidateHulls = () => this.placer.invalidateHulls();
    this.boundOnGeomLoaded = this.#onGeomLoaded.bind(this);
    this.debouncedInvalidatePlacement = debounce(this.#invalidatePlacement.bind(this), 67 /* 4 frames at 60fps */);

    this.globalTarget = new GlobalLabelUI(this.hud, this);
    this.globalTarget.init(this.dom);
  }

  clear() {
    this.globalTarget.dispose();
    this.globalTarget = null;
    this.labelById.forEach((l) => l.dispose());
    this.labelById.clear();
    this.filteredLevels = [];
  }

  dispose() {
    this.clear();
    this.viewer.removeEventListener(DT_LOADER_IDLE_EVENT, this.boundOnGeomLoaded);
    this.viewer.removeEventListener(av.MODEL_UNLOADED_EVENT, this.boundInvalidateHulls);
    this.debouncedInvalidatePlacement.cancel();
  }

  init() {
    this.viewer.addEventListener(DT_LOADER_IDLE_EVENT, this.boundOnGeomLoaded);
    this.viewer.addEventListener(av.MODEL_UNLOADED_EVENT, this.boundInvalidateHulls);
    return this;
  }

  /** Returns "targetable" levels when deferring asset labels. */
  getTargets() {
    if (!this.isOn()) return [];

    const targets = [];
    this.facility.facetsManager.getFacet(FacetTypes.levels).forEach((l) => !l.isUnassignedId && targets.push(l.id));
    return targets;
  }

  /** Level label placement is derived from the camera's perspective. */
  onCameraChanged( /* camera */) {
    // This is not using the debounced update as it uses the cached version.
    this.#invalidatePlacement(true);
  }

  onFacetsUpdated(event) {

    const levelsFacetDef = this.facility.facetsManager.getFacetDef(FacetTypes.levels);
    const filterSize = levelsFacetDef.filter.size;

    if (event.addedModel || filterSize !== this.filteredLevels.length) {
      this.#invalidateFilteredLevels();

      if (!levelsFacetDef) return;
    }

    const hasSelected = this.filteredLevels[0]?.selected;
    for (const label of this.labelById.values()) {
      label.setSelected(levelsFacetDef.filter.has(label.data.node.id), hasSelected);
    }
  }

  onFacetsLoaded(event) {
    this.#invalidateFilteredLevels();
  }

  async setVisible(visible) {

    const then = super.setVisible(visible);

    if (visible) {
      // If we were off, we don't know about our latest placement.
      this.#invalidatePlacement();
    } else {
      await then;
      this.invalidate();
    }

    // Reinitialize rollups targets for this layer
    const levelsRollups = this.hud.layers.compositor.rollups.rollups.levels;
    levelsRollups.clear();
    this.getTargets().forEach((id) => levelsRollups.set(id, {}));

    this.facility.facetsManager.facetsEffects?.setFloorBoundaryVizEnabled(visible);
    this.facility.facetsManager.highlightLocations();
    this.viewer.impl.invalidate(true);
    return then;
  }

  onClusterTransition(type, layerPrefs, isClustered) {
    // Don't re-enable levels layer when clustered
    if (type === av.TRANSITION_ENDED && isClustered) {
      return this.isVisible() && this.setVisible(false);
    }

    super.onClusterTransition(type, layerPrefs, isClustered);
  }

  onModelChanged({ change }) {
    const { modelId, dbIds } = change;
    if (!modelId || !dbIds) return;

    const model = this.facility.getModelByUrn(modelId);

    for (const facetItem of this.filteredLevels) {
      const ids = facetItem.ids.get(model);
      if (ids) {
        const found = dbIds.some((id) => sortedArrayHas(ids, id));
        if (found) {
          this.#invalidateFilteredLevels();
          return;
        }
      }
    }
  }

  onDblClick(event, level) {
    const bounds = getLevelBounds(this.facility, level);
    flyToTopOfBox(this.facility.viewer, bounds, 1.0);

    event.preventDefault();
    event.stopPropagation();
  }

  update(ts) {
    if (!this.isVisible()) return;

    this.isDirty = false;

    // We always need to redraw because collision is handled during composition
    return true;
  }

  *uis() {
    yield this.globalTarget;
    yield* this.labelById.values();
  }

  #invalidateFilteredLevels() {
    this.filteredLevels.length = 0;

    const levels = this.facility.facetsManager.getFacet(FacetTypes.levels);

    // Consider baking this into the facet
    const elevByName = getLevelElevationByName(this.facility);

    for (const level of levels) {
      if (level.isUnassignedId) continue;
      const filteredLevel = { ...level, avgElev: elevByName[level.id] };
      this.filteredLevels.push(filteredLevel);
    }

    // Sort by filtered levels so that we keep the "selected" labels with highest priority.
    this.filteredLevels.sort(byElevation);

    // Ensure either a cached hull exists for cached placement or we attempt to load one.
    this.debouncedInvalidatePlacement();
  }

  #invalidatePlacement(useCacheOnly) {
    const toRemove = new Map(this.labelById);
    for (const level of this.filteredLevels) {
      const placement = this.placer.guessPlacement(this.facility, level, level.avgElev, useCacheOnly);
      if (!placement) continue;

      let label = this.labelById.get(level.id);
      if (!label) {
        label = new LevelLabelUI(this.hud, this, placement.left, level);
        label.init(this.dom);
        label.setEstimatedSize(this.placer.measureCtx.measureText(level.id));
        label.setSelected(level.selected, this.filteredLevels[0]?.selected);
        this.labelById.set(level.id, label);
      } else {
        label.position.setPos3D(placement.left);
      }

      toRemove.delete(level.id);
    }

    for (const [key, label] of toRemove) {
      label.setVisible(false);
      label.dispose();
      this.labelById.delete(key);
    }

    this.invalidate();
  }

  #onGeomLoaded() {
    // On final frame is needed here as invalidate placement relies on a floor cached mesh which can change
    // as relevant geometries are loaded. This allows on camera move to instead used the cached version since this
    // geom loaded will take care of requesting a non-cached re-computation when needed.
    this.debouncedInvalidatePlacement();
  }

  compose(ctx, priority) {
    // Level labels are only composed at normal priority (for now?)
    if (priority !== PRIORITY.NORMAL) return;

    // For simplicity, manage the global target here as well.
    this.globalTarget.updateWithDeferred(ctx.rollups.getDeferredForGlobal());

    // Skip further composition if we don't have levels to begin with.
    if (!this.labelById.size) return;

    const uisIter = this.uis();
    uisIter.next(); // skip global target

    const deprioritizedUIs = [];

    // First pass to deal with deferred uis
    for (const ui of uisIter) {
      // Uncollapse all UIs by default
      ui.setCollapsible(false);

      let target = ui.getCollisionTarget();
      if (!ctx.onScreen(target)) {
        // TODO: Could defer the label's "children" to global?
        ui.setVisible(false);
        continue;
      }

      // Deferral status will be used to determine if the label is collapsible
      // as well as the estimated size for collision
      const deferred = ctx.rollups.getDeferredForLevel(ui.data.node.id);
      ui.updateWithDeferred(deferred);

      const hasCollisions = ctx.collisionTree.hasCollisions(target);
      if (hasCollisions) {
        // UI collides with previously placed uis, collapse it
        ui.setCollapsible(true);
        target = ui.getCollisionTarget();

        const collisions = ctx.collisionTree.getCollisions(target);
        for (let collision of collisions) {
          // Already collapsed, or non-collapsible, skip
          if (collision.ui.isCollapsed || !collision.ui.setCollapsible) continue;

          // Collapse colliding UIs and reinsert them
          ctx.collisionTree.remove(collision);
          collision.ui.setCollapsible(true);
          collision = collision.ui.getCollisionTarget();
          ctx.collisionTree.insert(collision);
        }
      }

      if (!deferred.length && !ui.isSelected) {
        // Collect non-selected, non-deferred uis
        deprioritizedUIs.push(ui);
        continue;
      }

      // Forces level labels visible if they have any deferred assets details,
      // even if they may collide with one another
      ui.setVisible(true);
      ctx.place(target);
    }

    // Second pass to deal with non-selected, non-deferred uis
    for (const ui of deprioritizedUIs) {
      const target = ui.getCollisionTarget();
      const hasCollisions = ctx.collisionTree.hasCollisions(target);
      if (hasCollisions) {
        // If there are still collisions after the first pass, hide it
        ui.setVisible(false);
      } else {
        ui.setVisible(true);
        ctx.place(target);
      }
    }
  }
}

const _lvlBounds = new THREE.Box3();
const _bounds = new THREE.Box3();
export function getLevelBounds(facility, level) {

  const cachedMesh = facility.facetsManager.facetsEffects.getFloorMesh(level);
  if (cachedMesh?.geometry) {
    return cachedMesh.geometry.boundingBox.clone().applyMatrix4(cachedMesh.matrix);
  }

  // Slow code path, bounds from everything inside the level.
  _lvlBounds.makeEmpty();
  for (const [model, ids] of level.ids) {
    for (const dbId of ids) {
      model.getElementBounds(dbId, _bounds);
      _lvlBounds.union(_bounds);
    }
  }

  return _lvlBounds;
}